import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/cn';

import { IconButton } from './Button';

const WIDTHS = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
} as const;

/**
 * Centred dialog, or a bottom sheet on phones. Escape closes, the backdrop closes, body scroll is locked
 * while open, and focus moves into the panel so keyboard users aren't left
 * behind on the page underneath.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: keyof typeof WIDTHS;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    // Prefer the first real input; fall back to the panel itself.
    const focusTarget =
      panelRef.current?.querySelector<HTMLElement>(
        'input:not([type=hidden]), textarea, select, [data-autofocus]'
      ) ?? panelRef.current;
    focusTarget?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto sm:items-start sm:p-8">
      <div
        className="fixed inset-0 animate-fade-in bg-[#0a0c18]/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative w-full border border-line bg-surface shadow-pop outline-none',
          'animate-sheet-in rounded-t-3xl sm:my-auto sm:animate-pop-in sm:rounded-3xl',
          WIDTHS[width]
        )}
      >
        <span
          className="mx-auto mt-2.5 block h-1 w-10 rounded-full bg-line sm:hidden"
          aria-hidden="true"
        />
        <header className="flex items-start justify-between gap-4 px-6 pb-3 pt-4 sm:pt-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
            {description && <p className="mt-0.5 text-[13px] text-muted">{description}</p>}
          </div>
          <IconButton icon={X} label="Close" size="sm" onClick={onClose} />
        </header>

        <div className="max-h-[70vh] overflow-y-auto scroll-thin px-6 py-3 sm:max-h-[65vh]">
          {children}
        </div>

        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-line bg-elevated/40 px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:rounded-b-3xl sm:pb-4">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}
